import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Table, Pagination, Badge, Input, Select, Button, Modal } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import type { CanaryWithStats, AdminOrganizationResponse } from '@spoofio/shared/types'

type CanaryRow = CanaryWithStats & { organizationName: string }

const DEBOUNCE_MS = 300

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active Only' },
  { value: 'false', label: 'Inactive Only' },
]

export function AdminCanariesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createOrgId, setCreateOrgId] = useState('')
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createDomains, setCreateDomains] = useState('')
  const [createScreenshot, setCreateScreenshot] = useState(false)
  const [createThreshold, setCreateThreshold] = useState(70)

  const searchParam = searchParams.get('search') ?? ''
  const orgParam = searchParams.get('orgId') ?? ''
  const activeParam = searchParams.get('isActive') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [searchInput, setSearchInput] = useState(searchParam)
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam)
  const [orgFilter, setOrgFilter] = useState(orgParam)
  const [activeFilter, setActiveFilter] = useState(activeParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setCurrentPage(1)
    }, DEBOUNCE_MS)
    return () => { clearTimeout(timer) }
  }, [searchInput])

  const syncParams = useCallback(
    (search: string, orgId: string, isActive: string, page: number) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (orgId) params.set('orgId', orgId)
      if (isActive) params.set('isActive', isActive)
      if (page > 1) params.set('page', String(page))
      setSearchParams(params)
    },
    [setSearchParams]
  )

  useEffect(() => {
    syncParams(debouncedSearch, orgFilter, activeFilter, currentPage)
  }, [debouncedSearch, orgFilter, activeFilter, currentPage, syncParams])

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data: orgsData } = useQuery({
    queryKey: ['admin-organizations-dropdown'],
    queryFn: () => api.adminListOrganizations({ limit: 200 }),
    staleTime: 60_000,
  })

  const createCanary = useMutation({
    mutationFn: () =>
      api.adminCreateCanary({
        organizationId: createOrgId,
        name: createName,
        description: createDescription || undefined,
        authorizedDomains: createDomains
          .split(/[,\n]/)
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        screenshotEnabled: createScreenshot,
        confidenceThreshold: createThreshold,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-canaries'] })
      setShowCreateModal(false)
      resetCreateForm()
    },
  })

  const resetCreateForm = () => {
    setCreateOrgId('')
    setCreateName('')
    setCreateDescription('')
    setCreateDomains('')
    setCreateScreenshot(false)
    setCreateThreshold(70)
  }

  const isActiveParam = activeFilter === 'true'
    ? true
    : activeFilter === 'false'
      ? false
      : undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-canaries', debouncedSearch, orgFilter, activeFilter, offset],
    queryFn: () =>
      api.adminListCanaries({
        search: debouncedSearch || undefined,
        orgId: orgFilter || undefined,
        isActive: isActiveParam,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrgFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleActiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveFilter(e.target.value)
    setCurrentPage(1)
  }

  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsData?.data.map((o: AdminOrganizationResponse) => ({
      value: o.id,
      label: o.name,
    })) ?? []),
  ]

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (c: CanaryRow) => (
        <span className="font-medium text-gray-900">{c.name}</span>
      ),
    },
    {
      key: 'token',
      header: 'Token',
      render: (c: CanaryRow) => (
        <span className="font-mono text-xs text-gray-500">
          {c.token.slice(0, 8)}...
        </span>
      ),
    },
    {
      key: 'organizationName',
      header: 'Organization',
      render: (c: CanaryRow) => c.organizationName,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (c: CanaryRow) => (
        <Badge variant={c.isActive ? 'success' : 'gray'}>
          {c.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'detections24h',
      header: 'Detections (24h)',
      render: (c: CanaryRow) => c.detectionCount24h,
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Canaries</h1>
        <Button size="sm" onClick={() => { setShowCreateModal(true) }}>
          Create Canary
        </Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Input
            label="Search"
            placeholder="Search by name or token..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value) }}
            className="w-64"
          />
          <Select
            label="Organization"
            value={orgFilter}
            onChange={handleOrgChange}
            options={orgOptions}
            className="w-48"
          />
          <Select
            label="Status"
            value={activeFilter}
            onChange={handleActiveChange}
            options={ACTIVE_OPTIONS}
            className="w-40"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load canaries</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(c) => c.id}
              onRowClick={(c) => { navigate(`/canaries/${c.id}`) }}
              isLoading={isLoading}
              emptyMessage="No canaries found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetCreateForm() }}
        title="Create Canary"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowCreateModal(false); resetCreateForm() }}>
              Cancel
            </Button>
            <Button
              isLoading={createCanary.isPending}
              disabled={!createOrgId || !createName || !createDomains.trim()}
              onClick={() => { createCanary.mutate() }}
            >
              Create
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Select
            label="Organization"
            value={createOrgId}
            onChange={(e) => { setCreateOrgId(e.target.value) }}
            options={[
              { value: '', label: 'Select organization...' },
              ...(orgsData?.data.map((o: AdminOrganizationResponse) => ({
                value: o.id,
                label: o.name,
              })) ?? []),
            ]}
          />
          <Input
            label="Name"
            value={createName}
            onChange={(e) => { setCreateName(e.target.value) }}
            placeholder="e.g. Donation Page"
          />
          <Input
            label="Description (optional)"
            value={createDescription}
            onChange={(e) => { setCreateDescription(e.target.value) }}
            placeholder="Short description"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Authorized Domains (comma or newline separated)
            </label>
            <textarea
              value={createDomains}
              onChange={(e) => { setCreateDomains(e.target.value) }}
              placeholder="example.com, cdn.example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="create-screenshot"
              checked={createScreenshot}
              onChange={(e) => { setCreateScreenshot(e.target.checked) }}
              className="rounded border-gray-300"
            />
            <label htmlFor="create-screenshot" className="text-sm text-gray-700">
              Enable screenshots
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confidence Threshold: {createThreshold}
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={createThreshold}
              onChange={(e) => { setCreateThreshold(Number(e.target.value)) }}
              className="w-full"
            />
          </div>
          {createCanary.error && (
            <p className="text-sm text-danger-600">
              {createCanary.error instanceof Error ? createCanary.error.message : 'Failed to create canary'}
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}
